<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.driver_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openAddDriver">
                {{ $t('labels.driverModule.addDriver') }}
            </hts-form-button>
        </div>
        <hts-data-table
            v-if="checkPermission(layoutPermissions.driver_management)"
            ref="dataTables"
            :headers="driverHeaders"
            :server="apiListdriver"
            use-selection
            :use-archive="useDriverArchive"
            :use-restore="useDriverRestore"
            store-name="driverDataTable"
            store-key="Driver" />
        <add-driver
            v-if="checkPermission(layoutPermissions.driver_add)"
            ref="addDriver"
            :show="showSlideOverAddDriver"
            @refresh-tables="refreshTables()"
            @close="showSlideOverAddDriver = false" />
        <document-driver
            v-if="checkPermission(layoutPermissions.driver_document_management)"
            ref="driverDocument" />
        <edit-driver
            v-if="checkPermission(layoutPermissions.driver_edit)"
            ref="editDriver"
            @refresh-tables="refreshTables()" />
        <driver-license
            v-if="checkPermission(layoutPermissions.driver_license_management)"
            ref="driverLicense"
            @refreshTableDriver="refreshTables()" />
        <hts-modal
            v-if="checkPermission(layoutPermissions.driver_delete)"
            :show="showPopupDeleteDriver"
            :title="$t('labels.deleteDriver')"
            type="confirm"
            :content="$t('labels.confirmDeleteDriver')"
            @close="closePopupDeleteDriver"
            @confirm="() => actionDeleteDriver()" />
        <hts-modal
            v-if="checkPermission(layoutPermissions.driver_edit)"
            :show="showPopupChangeStatusDriver"
            :title="$t('labels.statusChanges')"
            type="confirm"
            :content="$t('labels.statusChangesContent')"
            @hide="closePopupChangeStatusDriver"
            @confirm="() => actionChangesStatusDriver()" />
    </div>
</template>

<script lang="ts" setup>
import Actions from './ignore/actions.vue'
import AddDriver from './ignore/add-driver.vue'
import DocumentDriver from './ignore/document.vue'
import EditDriver from './ignore/edit-driver.vue'
import { IDriver } from './ignore/imports/form'
import DriverLicense from './ignore/license.vue'
import Status from '~/components/status.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

const { $formatters } = useNuxtApp()

const dataTables = ref()
const codeDriver = ref()
const dataChangeStatus = ref()
const valueStatus = ref()

const apiListdriver = $url.driver.driver['get-list']
const showSlideOverAddDriver = ref(false)
const showPopupDeleteDriver = ref(false)
const showPopupChangeStatusDriver = ref(false)
const driverIsSelectedForDelete: any = reactive<IDriver>({} as IDriver)
const driverLicense = ref()
const driverDocument = ref()
const addDriver = ref()
const editDriver = ref()

const handleViewDocument = (driver: any) => {
  if (!driver) {
    return null
  }
  driverDocument.value.open(driver._id)
}

const handleEditDriver = (driver: any) => {
  if (!driver) {
    return null
  }

  editDriver.value.open(driver._id)
}

const handleDeleteDriver = (driver: any) => {
  if (!driver) {
    return null
  }
  showPopupDeleteDriver.value = true
  Object.assign(driverIsSelectedForDelete, driver)
}

const handleViewLicense = (driver: any) => {
  if (!driver) {
    return null
  }
  driverLicense.value.open(driver._id)
}

const openAddDriver = () => {
  if (addDriver?.value) {
    addDriver.value.open()
  }
}

const useDriverArchive = { api: $url.driver.driver.archive, permissions: checkPermission(layoutPermissions.driver_archive) }
const useDriverRestore = { api: $url.driver.driver.restore, permissions: checkPermission(layoutPermissions.driver_restore) }

const actionChangesStatusDriver = () => {
  const api = $url.driver.driver.changeStatus
  const data = {
    id: dataChangeStatus.value.data._id,
    status: valueStatus.value
  }
  $api(api, { body: data }).then((data) => {
    const resData: any = data?.data?.value
    if (resData?.success) {
      process.client && $toast().success($t('labels.updateSuccess'))
      refreshTables()
      closePopupChangeStatusDriver()
    }
  })
}
const actionDeleteDriver = () => {
  const api = $url.driver.driver.delete

  $api(api, { body: { ids: [driverIsSelectedForDelete._id] } }).then((data: any) => {
    const resData: any = data?.data?.value
    if (resData?.success) {
      Object.assign(driverIsSelectedForDelete, {})
      refreshTables()
      showPopupDeleteDriver.value = false
      process.client && $toast().success($t('labels.deleteSuccess'))
    }
  })
}

const closePopupDeleteDriver = () => {
  showPopupDeleteDriver.value = false
  Object.assign(driverIsSelectedForDelete, {})
}

const closePopupChangeStatusDriver = () => {
  showPopupChangeStatusDriver.value = false
  Object.assign(driverIsSelectedForDelete, {})
}

const driverHeaders = [
  {
    field: 'fullName',
    headerName: $t('labels.fullName'),
    resizable: true,
    sortable: true,
    minWidth: 180,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'idCardNumber',
    headerName: $t('labels.driverModule.idCardNumber'),
    resizable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'phone',
    headerName: $t('labels.phone'),
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      const code = params?.data?.phone.code || ''
      const phone = params?.data?.phone.phone || ''
      return code ? `(${code}) ${phone}` : phone
    }
  },
  {
    field: 'email',
    headerName: $t('labels.email'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicleModule.licensePlate'),
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      if (params?.data.vehicleDetail) {
        return params?.data.licensePlate
      }

      if (!params.data.licensePlate) {
        return ''
      }

      return `${params.data.licensePlate} - Lỗi đồng bộ với xe`
    }
  },
  {
    field: 'licenseClass',
    minWidth: 180,
    headerName: $t('labels.driverModule.licenseType'),
    valueGetter: (params: any) => {
      return params.data.licenseClass
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'driver',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: $t('labels.notReadyYet'), value: 0, class: { '!bg-red-100': true, '!text-red-800': true } }, // Not Ready Yet: Màu đỏ
      { name: $t('labels.conditionallyReady'), value: 1, class: { '!bg-green-100': true, '!text-green-800': true } }, // Conditionally Ready: Màu xanh lá cây
      { name: $t('labels.ready'), value: 2, class: { '!bg-green-100': true, '!text-green-800': true } } // Ready: Màu xanh lá cây
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'driverDataTable',
      storeName: 'Driver',
      items: [
        // {
        //   icon: 'hi-eye-icon',
        //   tooltip: $t('labels.viewDetail'),
        //   class: 'text-blue-600 hover:text-blue-500',
        //   permissions: checkPermission(layoutPermissions.driver_management)
        //   // click: handleViewDetail
        // },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_edit),
          click: handleEditDriver
        },
        {
          icon: 'hi-document-text-icon',
          label: $t('labels.viewDocument'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_document_management),
          click: handleViewDocument
        },
        {
          icon: 'hi-document-check-icon',
          label: $t('labels.driverModule.license.license'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.driver_license_management),
          click: handleViewLicense
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.driverModule.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.driver_delete),
          click: handleDeleteDriver,
          isArchive: true
        }
      ]
    }
  }
]

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

function formatDate (date: Date) {
  const d = new Date(date)
  let month = '' + (d.getMonth() + 1)
  let day = '' + d.getDate()
  const year = d.getFullYear()

  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  return [year, month, day].join('-')
}
</script>
